class OkazoConditionTabs extends HTMLElement {
  connectedCallback() {
    if (this.connection) return;
    this.connection = new AbortController();
    const { signal } = this.connection;

    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));

    this.tabs.forEach((tab) => {
      tab.addEventListener("click", () => this.select(tab), { signal });
      tab.addEventListener("keydown", (event) => this.onKeydown(event), { signal });
    });
  }

  select(selectedTab) {
    this.tabs.forEach((tab) => {
      const isSelected = tab === selectedTab;
      tab.setAttribute("aria-selected", String(isSelected));
      tab.tabIndex = isSelected ? 0 : -1;
    });

    this.panels.forEach((panel) => {
      panel.classList.toggle("is-active", panel.id === selectedTab.getAttribute("aria-controls"));
    });
  }

  onKeydown(event) {
    const next = okazoTabIndex(event, this.tabs);
    if (next === null) return;

    this.tabs[next].focus();
    this.select(this.tabs[next]);
  }

  disconnectedCallback() {
    this.connection?.abort();
    this.connection = null;
  }
}

if (!customElements.get("okazo-condition-tabs")) {
  customElements.define("okazo-condition-tabs", OkazoConditionTabs);
}

class OkazoProductRail extends HTMLElement {
  constructor() {
    super();
    this.onRailScroll = this.queueProgressUpdate.bind(this);
    this.onViewportResize = this.queueProgressUpdate.bind(this);
  }

  connectedCallback() {
    if (this.connection) return;
    this.connection = new AbortController();
    const { signal } = this.connection;

    this.section = this.closest(".okz-featured");
    this.panel = this.closest("[data-okz-featured-panel]");
    this.railFrame = this.closest(".okz-featured__rail-frame");
    this.controls = Array.from((this.panel || this.section)?.querySelectorAll("[data-okz-rail-direction]") || []);
    this.status =
      this.panel?.querySelector("[data-okz-product-rail-status]") ||
      this.section?.querySelector("[data-okz-product-rail-status]");
    this.progress =
      this.panel?.querySelector("[data-okz-product-rail-progress]") ||
      this.section?.querySelector("[data-okz-product-rail-progress]");
    this.grid = this.querySelector(":scope > .okz-product-grid");
    this.pointerStartX = null;
    this.pointerStartY = null;
    this.scrollStartX = 0;
    this.activePointerId = null;
    this.wasDragged = false;
    this.isDragging = false;
    this.isGestureLocked = false;
    this.progressFrame = null;

    this.querySelectorAll("img").forEach((image) => {
      image.draggable = false;
    });
    this.controls.forEach((control) => {
      control.addEventListener("click", () => this.scrollRail(Number(control.dataset.okzRailDirection)), { signal });
    });
    this.addEventListener(
      "keydown",
      (event) => {
        if (event.target !== this || !["ArrowLeft", "ArrowRight"].includes(event.key)) return;
        event.preventDefault();
        this.scrollRail(event.key === "ArrowLeft" ? -1 : 1);
      },
      { signal },
    );
    this.addEventListener("pointerdown", (event) => this.onPointerDown(event), { signal });
    this.addEventListener("pointermove", (event) => this.onPointerMove(event), { signal });
    this.addEventListener("pointerup", (event) => this.onPointerUp(event), { signal });
    this.addEventListener("pointercancel", (event) => this.onPointerUp(event), { signal });
    this.addEventListener("dragstart", (event) => event.preventDefault(), { signal });
    this.addEventListener("scroll", this.onRailScroll, { signal, passive: true });
    this.addEventListener(
      "click",
      (event) => {
        if (!this.wasDragged) return;
        event.preventDefault();
        event.stopPropagation();
        this.wasDragged = false;
      },
      { capture: true, signal },
    );
    this.queueProgressUpdate();
    if ("ResizeObserver" in window) {
      this.progressObserver = new ResizeObserver(() => this.queueProgressUpdate());
      this.progressObserver.observe(this);
      if (this.grid) this.progressObserver.observe(this.grid);
    } else {
      window.addEventListener("resize", this.onViewportResize, { signal });
    }
  }

  disconnectedCallback() {
    this.connection?.abort();
    this.connection = null;

    this.progressObserver?.disconnect();

    if (this.progressFrame) window.cancelAnimationFrame(this.progressFrame);
  }

  scrollRail(direction) {
    const card = this.querySelector(":scope > .okz-product-grid > :first-child");
    const gap = Number.parseFloat(getComputedStyle(this.querySelector(":scope > .okz-product-grid")).columnGap) || 0;
    const distance = card ? card.getBoundingClientRect().width + gap : this.clientWidth * 0.8;
    this.scrollBy({
      left: direction * distance,
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
    });
  }

  queueProgressUpdate() {
    if (!this.progress) return;
    if (this.progressFrame) return;
    this.progressFrame = window.requestAnimationFrame(() => {
      this.progressFrame = null;
      this.updateProgress();
    });
  }

  updateProgress() {
    if (!this.progress) return;
    const scrollableDistance = Math.max(0, this.scrollWidth - this.clientWidth);
    this.controls.forEach((control) => {
      control.disabled =
        Number(control.dataset.okzRailDirection) < 0 ? this.scrollLeft <= 1 : this.scrollLeft >= scrollableDistance - 1;
    });
    const visibleRatio = this.scrollWidth > 0 ? this.clientWidth / this.scrollWidth : 1;
    const thumbSize = scrollableDistance > 1 ? Math.max(22, Math.min(100, visibleRatio * 100)) : 100;
    const thumbOffset =
      scrollableDistance > 1 ? (Math.max(0, this.scrollLeft) / scrollableDistance) * (100 - thumbSize) : 0;

    this.progress.style.setProperty("--okz-rail-progress-size", `${thumbSize}%`);
    this.progress.style.setProperty("--okz-rail-progress-offset", `${thumbOffset}%`);
    this.railFrame?.classList.toggle("is-rail-static", scrollableDistance <= 1);
  }

  onPointerDown(event) {
    if (event.button !== 0) return;
    this.activePointerId = event.pointerId;
    this.pointerStartX = event.clientX;
    this.pointerStartY = event.clientY;
    this.scrollStartX = this.scrollLeft;
    this.wasDragged = false;
    this.isDragging = false;
    this.isGestureLocked = false;
  }

  onPointerMove(event) {
    if (this.activePointerId !== event.pointerId) return;
    const delta = event.clientX - this.pointerStartX;
    const deltaY = event.clientY - this.pointerStartY;
    const absX = Math.abs(delta);
    const absY = Math.abs(deltaY);

    if (!this.isGestureLocked && (absX > 7 || absY > 7)) {
      this.isGestureLocked = true;
      this.isDragging = event.pointerType !== "touch" || absX > absY * 1.15;
    }

    if (this.isDragging) {
      this.wasDragged = true;
      if (!this.hasPointerCapture?.(event.pointerId)) this.setPointerCapture?.(event.pointerId);
      this.classList.add("is-dragging");
      this.grid?.classList.add("is-dragging");
      this.scrollLeft = this.scrollStartX - delta;
      event.preventDefault();
    }
  }

  onPointerUp(event) {
    if (event && this.activePointerId !== event.pointerId) return;
    if (event && this.hasPointerCapture?.(event.pointerId)) this.releasePointerCapture(event.pointerId);
    this.activePointerId = null;
    this.pointerStartX = null;
    this.pointerStartY = null;
    this.isDragging = false;
    this.isGestureLocked = false;
    this.classList.remove("is-dragging");
    this.grid?.classList.remove("is-dragging");
  }
}

if (!customElements.get("okazo-product-rail")) {
  customElements.define("okazo-product-rail", OkazoProductRail);
}

class OkazoFeaturedTabs extends HTMLElement {
  connectedCallback() {
    if (this.connection) return;
    this.connection = new AbortController();
    const { signal } = this.connection;

    this.tabs = Array.from(this.querySelectorAll("[data-okz-featured-tab]"));
    this.panels = Array.from(this.querySelectorAll("[data-okz-featured-panel]"));

    this.tabs.forEach((tab) => {
      tab.addEventListener("click", () => this.select(tab, false), { signal });
      tab.addEventListener("keydown", (event) => this.onKeydown(event), { signal });
    });

    const selectedTab =
      this.tabs.find((tab) => tab.getAttribute("aria-selected") === "true") ||
      this.tabs.find((tab) => !tab.disabled) ||
      this.tabs[0];

    if (selectedTab) this.select(selectedTab, false);
  }

  select(selectedTab, focus) {
    const selectedPanelId = selectedTab.getAttribute("aria-controls");

    this.tabs.forEach((tab) => {
      const isSelected = tab === selectedTab;
      tab.setAttribute("aria-selected", String(isSelected));
      tab.tabIndex = isSelected ? 0 : -1;
    });

    let selectedPanel = null;
    this.panels.forEach((panel) => {
      const isSelected = panel.id === selectedPanelId;
      panel.hidden = !isSelected;
      panel.classList.toggle("is-active", isSelected);
      if (isSelected) selectedPanel = panel;
    });

    if (focus) {
      selectedTab.focus({ preventScroll: true });
      const tabList = selectedTab.closest('[role="tablist"]');
      const tabBounds = selectedTab.getBoundingClientRect();
      const listBounds = tabList.getBoundingClientRect();
      const listStyle = getComputedStyle(tabList);
      const visibleLeft = listBounds.left + parseFloat(listStyle.paddingLeft);
      const visibleRight = listBounds.right - parseFloat(listStyle.paddingRight);
      const offset =
        tabBounds.left < visibleLeft ? tabBounds.left - visibleLeft : Math.max(0, tabBounds.right - visibleRight);
      tabList.scrollBy({ left: offset, behavior: "instant" });
    }
    this.refreshPanel(selectedPanel);
  }

  onKeydown(event) {
    const next = okazoTabIndex(event, this.tabs);
    if (next === null) return;

    this.select(this.tabs[next], true);
  }

  refreshPanel(panel) {
    if (!panel) return;
    const rail = panel.querySelector("okazo-product-rail");
    if (rail?.queueProgressUpdate) {
      window.requestAnimationFrame(() => rail.queueProgressUpdate());
    }
  }

  disconnectedCallback() {
    this.connection?.abort();
    this.connection = null;
  }
}

if (!customElements.get("okazo-featured-tabs")) {
  customElements.define("okazo-featured-tabs", OkazoFeaturedTabs);
}

if (window.Shopify?.designMode) {
  document.addEventListener("shopify:block:select", (event) => {
    const tab = event.target?.matches?.("[data-okz-featured-tab]")
      ? event.target
      : event.target?.querySelector?.("[data-okz-featured-tab]");
    const tabs = tab?.closest?.("okazo-featured-tabs");
    if (tab && tabs?.select) tabs.select(tab, true);
  });
}
